import { ChevronRightIcon, DatabaseIcon } from '@heroicons/react/outline';
import ChartIcon from 'assets/svg/icons/ChartIcon';
import Spreadsheet from 'assets/svg/icons/Spreadsheet';
import classNames from 'classnames';
import Anchor from 'components/atoms/Anchor';
import { Menu } from 'components/atoms/Menu';
import ExportIcon from 'components/atoms/SVG/sidebarIcons/ExportIcon.component';
import MoreIcon from 'components/atoms/SVG/sidebarIcons/MoreIcon.component';
import useFeatures from 'hooks/useFeatures';
import useGetCustomNavigationLink from 'hooks/useGetCustomNavigationLink';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { WEB_MENU_CLICKEDKNOWLEDGECENTER } from 'utils/amplitude/events/global.amplitude';
import { InfoIconOutline } from 'utils/icons/figmaIcons';
import convertMarkdown from 'utils/markdown';
import NavigationItem from './NavigationItem.component';
import { SidebarNavigationItemTypesEnum } from './types';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import { useIsBEFinFeatureEnabled } from 'utils/docsRepo/useIsBEFinFeatureEnabled/useIsBEFinFeatureEnabled';

const ItemUI: FC<{
  icon: React.ReactNode;
  title: string;
}> = ({ icon, title }) => (
  <span className="flex gap-2 items-center !text-primary-800 hover:!text-primary">
    <span className="w-6 flex items-center justify-center">{icon}</span>
    <span>{title}</span>
  </span>
);

const MoreMenu: FC = () => {
  const { t, i18n } = useTranslation();
  const getLink = useGetCustomNavigationLink();
  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();
  const isBEFinFeatureEnabled = useIsBEFinFeatureEnabled();
  // Show repository for: users without feature flag (german users or belgium users without feature flag)
  const shouldShowRepositoryInMoreMenu = !isBEFinFeatureEnabled;

  const showProfitAndLossInMoreMenu = useFeatures(
    'showProfitAndLossInMoreMenu',
  );
  return (
    <Menu
      buttonProps={{ id: 'MORE_MENU', className: 'w-full' }}
      position="top"
      trigger={(api: any) => {
        const isActive = api.open;

        return (
          <NavigationItem
            active={isActive}
            titleContainerClassName="flex-1"
            item={{
              title: '',
              renderTitle: () => (
                <span
                  className={classNames(
                    'flex flex-1 w-full items-center justify-between',
                    isActive ? '!text-primary' : '',
                  )}
                >
                  <span className="flex-1">{t('sidebar.more')}</span>
                  <span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </span>
                </span>
              ),
              Icon: MoreIcon,
              type: SidebarNavigationItemTypesEnum.more,
            }}
          />
        );
      }}
      rootClassName="w-full"
      contentWrapperClassName="left-[13rem] !ring-0 !px-1 !py-2 !rounded-xl !px-1 !w-60"
    >
      {shouldShowTaxAdvisor && (
        <Menu.Group className="!p-0 !border-t-0">
          <Anchor
            href={`https://help.accountable.eu/${i18n.language}`}
            className="flex w-full rounded-md px-2 py-2 text-sm font-semibold hover:!bg-primary-50 gap-2 !text-primary-800 hover:text-primary !items-start group"
            target="_blank"
            rel="noreferrer"
            tracingEvent={WEB_MENU_CLICKEDKNOWLEDGECENTER}
          >
            <span className="w-6 group-hover:!text-primary">
              <InfoIconOutline className="w-6" />
            </span>
            <span className="flex flex-col text-left">
              <span className="font-semibold block text-md group-hover:!text-primary flex gap-2 items-center">
                {t('sidebar.support.knowledge_center.title')}
              </span>
              <span className="text-xs text-primary-700 font-medium">
                {convertMarkdown(
                  t('sidebar.support.knowledge_center.description'),
                )}
              </span>
            </span>
          </Anchor>
        </Menu.Group>
      )}
      <Menu.Group className="!p-0">
        <Menu.Link
          className="hover:!bg-primary-50 "
          activeClassName="bg-primary-50 rounded-md"
          inactiveClassName=""
          to={getLink('/amortization')}
        >
          <ItemUI
            icon={<Spreadsheet className="w-5 h-5" viewBox="1 1 22 22" />}
            title={t('settings.amortization.title')}
          />
        </Menu.Link>
      </Menu.Group>
      <Menu.Group className="!p-0 !border-t-0">
        <Menu.Link
          className="hover:!bg-primary-50"
          activeClassName="bg-primary-50 rounded-md"
          inactiveClassName=""
          to={getLink('/export')}
        >
          <ItemUI
            icon={<ExportIcon className="w-5 h-5" viewBox="1 1 30 30" />}
            title={t('export.title_web')}
          />
        </Menu.Link>
      </Menu.Group>

      {shouldShowRepositoryInMoreMenu && (
        <Menu.Group className="!p-0 !border-t-0">
          <Menu.Link
            className="hover:!bg-primary-50"
            activeClassName="bg-primary-50 rounded-md"
            inactiveClassName=""
            to={getLink('/repository')}
          >
            <ItemUI
              icon={<DatabaseIcon className="w-5 h-5" />}
              title={t('repository.title')}
            />
          </Menu.Link>
        </Menu.Group>
      )}

      {showProfitAndLossInMoreMenu ? (
        <Menu.Group className="!p-0 !border-t-0">
          <Menu.Link
            className="hover:!bg-primary-50 "
            activeClassName="bg-primary-50 rounded-md"
            inactiveClassName=""
            to={getLink('/taxes/analytics')}
          >
            <ItemUI
              icon={<ChartIcon className="w-5 h-5" viewBox="1 1 22 22" />}
              title={t('profit_and_loss.title')}
            />
          </Menu.Link>
        </Menu.Group>
      ) : null}
    </Menu>
  );
};

export default MoreMenu;
